import { useMemo, useState } from 'react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAgentDashboard } from '../../../../api/agent-miner-client/agent-miner-client.hooks';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import {
  useTimeAllocation,
  useTimeAllocations,
} from '../../../../store/process-analysis-store/process-analysis-store.hooks';
import { skyLight } from '../../../../styles/design-tokens';
import { LineChartLegend } from '../../components/line-chart/line-chart-legend';
import { useItemColors } from '../../components/line-chart/line-chart.hooks';
import type { LineItem } from '../../components/line-chart/line-chart.type';
import { USAGE_SERIES } from '../filters/filters-content';
import { buildChartData } from './main-section.helpers';
const ALL_SERIES: LineItem[] = USAGE_SERIES.map((series) => ({ id: series.id, name: series.label }));
const PERCENTAGE_SERIES = new Set(['agent_adoption_rate', 'ai_code_percentage']);

export const UsageChart = () => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const organizationId = useGlobalStore((state) => state.organization?.id);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const startDate = useProcessAnalysisStore((state) => state.startDate);
  const endDate = useProcessAnalysisStore((state) => state.endDate);
  const agentVisibleSeries = useProcessAnalysisStore((state) => state.agentVisibleSeries);
  const agentChartType = useProcessAnalysisStore((state) => state.agentChartType);
  const timeAllocationType = useTimeAllocation();
  const timeAllocations = useTimeAllocations();

  const { data } = useAgentDashboard({
    organization_id: organizationId ?? '',
    portfolio_id: portfolioId ?? '',
    start_date: startDate,
    end_date: endDate,
    time_allocation_type: timeAllocationType,
  });

  const chartData = useMemo(
    () =>
      buildChartData({
        measures: {
          agent_adoption_rate: data?.agent_adoption?.agent_adoption_rate,
          agent_session_count_overtime: data?.agent_session_count_overtime,
          agent_event_count_overtime: data?.agent_event_count_overtime,
          ai_code_percentage: data?.ai_code_percentage,
        },
        timeAllocationType,
        timeAllocations,
      }),
    [
      data?.agent_adoption?.agent_adoption_rate,
      data?.agent_session_count_overtime,
      data?.agent_event_count_overtime,
      data?.ai_code_percentage,
      timeAllocationType,
      timeAllocations,
    ],
  );

  const selectedItems = useMemo(
    () => ALL_SERIES.filter((series) => agentVisibleSeries[series.id] ?? true),
    [agentVisibleSeries],
  );

  const countSeries = selectedItems.filter((item) => !PERCENTAGE_SERIES.has(item.id));
  const percentageSeries = selectedItems.filter((item) => PERCENTAGE_SERIES.has(item.id));

  const itemColors = useItemColors(selectedItems);
  const isHighlighted = (key: string) => hoveredItem === null || hoveredItem === key;

  const hasCountSeries = countSeries.length > 0;
  const hasPercentageSeries = percentageSeries.length > 0;

  return (
    <div style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={160}>
        <ComposedChart data={chartData} margin={{ top: 20, right: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          {hasCountSeries && (
            <YAxis
              yAxisId="count"
              orientation="left"
              tick={{ fontSize: 12 }}
              label={{
                value: 'Count',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle' },
                offset: 10,
              }}
            />
          )}
          {hasPercentageSeries && (
            <YAxis
              yAxisId="percentage"
              orientation={hasCountSeries ? 'right' : 'left'}
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
              tickFormatter={(value: number) => `${value}%`}
              label={{
                value: '%',
                angle: hasCountSeries ? 90 : -90,
                position: hasCountSeries ? 'insideRight' : 'insideLeft',
                style: { textAnchor: 'middle' },
                offset: 10,
              }}
            />
          )}
          <Tooltip
            labelFormatter={(label, payload) => payload?.[0]?.payload?.dateRange ?? label}
            formatter={(value, name) => {
              const seriesItem = ALL_SERIES.find((series) => series.id === name);
              const displayName = seriesItem?.name ?? String(name);
              if (PERCENTAGE_SERIES.has(String(name))) {
                return [`${value}%`, displayName];
              }
              return [value, displayName];
            }}
          />
          {countSeries.map((item) =>
            agentChartType === 'bar' ? (
              <Bar
                key={item.id}
                yAxisId="count"
                dataKey={item.id}
                name={item.id}
                fill={itemColors[item.id]}
                opacity={isHighlighted(item.id) ? 1 : 0.3}
              />
            ) : (
              <Line
                key={item.id}
                yAxisId="count"
                type="monotone"
                dataKey={item.id}
                name={item.id}
                stroke={itemColors[item.id]}
                strokeWidth={2}
                strokeOpacity={isHighlighted(item.id) ? 1 : 0.3}
                dot={{ r: 3, fill: itemColors[item.id] }}
                isAnimationActive={false}
                connectNulls
              />
            ),
          )}
          {percentageSeries.map((item) =>
            agentChartType === 'bar' ? (
              <Bar
                key={item.id}
                yAxisId="percentage"
                dataKey={item.id}
                name={item.id}
                fill={itemColors[item.id]}
                opacity={isHighlighted(item.id) ? 1 : 0.3}
              />
            ) : (
              <Line
                key={item.id}
                yAxisId="percentage"
                type="monotone"
                dataKey={item.id}
                name={item.id}
                stroke={itemColors[item.id]}
                strokeWidth={2}
                strokeOpacity={isHighlighted(item.id) ? 1 : 0.3}
                dot={{ r: 3, fill: itemColors[item.id] }}
                isAnimationActive={false}
                connectNulls
              />
            ),
          )}
          <Legend
            content={
              <LineChartLegend
                selectedItems={selectedItems}
                hoveredItem={hoveredItem}
                setHoveredItem={setHoveredItem}
                itemColors={itemColors}
                formatItemName={(name) => ALL_SERIES.find((series) => series.id === name)?.name ?? name}
                chartData={chartData}
              />
            }
            verticalAlign="bottom"
            height={36}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
